import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LayoutDashboard } from 'lucide-react';
import { parseAsString, useQueryStates } from 'nuqs';
import type { DashboardDto, DashboardTileDto, DashboardTileId } from '@pams/shared';
import { DASHBOARD_TILES, groupTilesByRow } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { PageHeader } from '@/components/PageHeader';
import { Loading, Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/hooks/useAuth';
import { api } from '@/lib/api';
import { DASHBOARD_ROW_GRID, DASHBOARD_ROW_STACK, TILE_SPAN } from '@/lib/dashboardGrid';
import { resolveTileWords } from '@/lib/tileWords';
import { hrefOf } from '@/lib/drillTarget';
import { asPickerDate, fromPickerDate } from '@/lib/pickerDate';
import { queryKeys } from '@/lib/queryKeys';
import { DASHBOARD_COPY } from './copy';
import { TileCard } from './TileCard';
import {
  hasRenderer,
  renderTile,
  tileDrillReach,
  tileIsDrillable,
  tileSkeleton,
  type TileActions,
} from './tiles/registry';
import { ArrangementPreviewModal } from '@/components/preview/ArrangementPreviewModal';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE SCOPED DASHBOARD (FR-5.5.*). [P6a]
 *
 *  ONE request returns the resolved layout AND every tile's data. Fourteen per-tile endpoints
 *  would be fourteen round trips and fourteen scope resolutions of the same subtree.
 *
 *  ⚠️ THE COLD START IS A PAGE-LEVEL SKELETON; EVERY LOAD AFTER IT IS PER-TILE, and that is
 *  an honest statement rather than a compromise. Before the first response the client does not
 *  know which tiles exist, how many, or their sizes — so a per-tile skeleton there is a GUESS
 *  at the layout that then reflows into a different shape. After it the layout is known (it is
 *  scope-derived, not range-derived), so a range change — the common case, since an operator
 *  lands once and then moves the picker — shows every tile its own silhouette and the grid
 *  never moves.
 *
 *  ⚠️ A PER-TILE RETRY RE-REQUESTS EXACTLY ONE TILE, through `?tiles=`. A "retry this tile"
 *  button that reloaded thirteen others would be a promise the network does not keep.
 *
 *  ⚠️ `setQueryData`, NEVER `invalidateQueries` — [P4]'s rule. It neither refetches nor marks
 *  stale, so there is no scheduled refetch for the retry to race; the mechanism is removed
 *  rather than an instance of it avoided. And the merge is BY POSITION, so a tile that is
 *  fixed does not jump to the end of the grid.
 *
 *  ⚠️ THE PAGE RENDERS `payload.tiles` IN THE SERVER'S ORDER AND NEVER ITERATES THE CATALOGUE.
 *  A tile outside this dashboard's scope is ABSENT rather than present-and-empty (FR-3.6), and
 *  the client has no mechanism to invent one.
 *
 *  ⚠️ AND THE DATE RANGE IS THE SERVER'S DEFAULT, NEVER WRITTEN INTO THE URL ON MOUNT. Baking
 *  today's date into every shared link opens a link shared at 23:59 on yesterday. The inbox's
 *  rule; the one difference is that this defaults to TODAY ALONE — a snapshot rather than a
 *  worklist.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const NUQS_OPTIONS = { history: 'replace', clearOnDefault: true } as const;

/** Position-preserving merge — see the header. */
const mergeTile = (previous: DashboardDto | undefined, fresh: DashboardDto): DashboardDto | undefined => {
  if (!previous) return fresh;
  const byId = new Map<DashboardTileId, DashboardTileDto>(fresh.tiles.map((tile) => [tile.id, tile]));
  return { ...previous, tiles: previous.tiles.map((tile) => byId.get(tile.id) ?? tile) };
};

const DashboardPage = () => {
  const { officer } = useAuth();
  const queryClient = useQueryClient();

  const [range, setRange] = useQueryStates(
    { dateFrom: parseAsString.withDefault(''), dateTo: parseAsString.withDefault('') },
    NUQS_OPTIONS,
  );

  const params = { dateFrom: range.dateFrom || undefined, dateTo: range.dateTo || undefined };

  const view = useQuery({
    queryKey: queryKeys.dashboard.view(params),
    queryFn: ({ signal }) => api.get<DashboardDto>('/dashboard', { query: params, signal }),
    /*
     * ⚠️ WITHOUT THIS THE "EVERY LOAD AFTER THE FIRST IS PER-TILE" CLAIM IS FALSE.
     *
     * A range change changes the query key, so the new entry has no data and the page would
     * fall back to the COLD-START skeleton — the grid emptying and rebuilding on every date
     * pick, which is the reflow the cold-start block exists to avoid. Keeping the previous
     * payload keeps the LAYOUT on screen while the numbers reload, so each tile shows its own
     * silhouette in place. `useServerTable` uses the same option for the same reason.
     */
    placeholderData: keepPreviousData,
  });

  const retry = useMutation({
    mutationFn: (id: DashboardTileId) => api.get<DashboardDto>('/dashboard', { query: { ...params, tiles: id } }),
    onSuccess: (fresh) => {
      queryClient.setQueryData<DashboardDto>(queryKeys.dashboard.view(params), (previous) =>
        mergeTile(previous, fresh),
      );
    },
  });

  /*
   * ⚠️ [C1e §1a/§2] THE ONE CHANNEL A RENDERER HAS TO THE HOST, and ONE modal for the whole
   * page rather than one per row — [PRE-P6.1] measured an overlay per list row at 64s → 119s.
   * `useMemo` so a renderer's props do not change identity on every tile refetch.
   */
  /*
   * ⚠️ [C1e §2] ONE modal for the whole page — [PRE-P6.1] measured an overlay per list row at
   * 64s → 119s on one test file — and the EYE TRAVELS WITH THE ID, so focus can go back to it
   * on close. One piece of state, so the two cannot disagree; a `useRef` here is what
   * `react-hooks/refs` refuses, and correctly, because the value must survive a re-render.
   */
  const [preview, setPreview] = useState<{ id: string; eyeKey: string } | null>(null);
  const actions = useMemo<TileActions>(() => ({ onPreview: (id, _title, eyeKey) => setPreview({ id, eyeKey }) }), []);

  const payload = view.data;
  const accessRole = officer?.accessRole ?? 'NONE';

  const offCount = payload ? payload.layout.filter((row) => row.available && row.placements === 0).length : 0;
  /*
   * ⚠️ [P6b] THE OVERRIDES RIDE ON `layout`, WHICH NAMES EVERY CATALOGUE ENTRY, while `tiles`
   * carries only the resolved ones with their data. Two arrays, one lookup — and the server
   * sends what the OPERATOR TYPED rather than a resolved English title, because a resolved
   * string on this wire would be English in a payload the officer app also reads.
   */

  return (
    <div className="flex flex-col gap-6">
      {/*
       * ⚠️ NO SUBTITLE, BY DECISION — the header is the title, the icon and the filters.
       *
       * It said two things and both survive elsewhere: the DEFAULT RANGE is on the date
       * pickers, which read `Today` when empty, so an operator is still told what they are
       * looking at without a sentence repeating it. ⚠️ **The SCOPE line did not survive**:
       * "Everything below is <unit> and the units beneath it" has no other home on this
       * screen, and a nodal now has to know whose subtree they are reading rather than being
       * told. `payload.scope.orgUnit` is still on the wire if it is ever wanted back.
       */}
      {/*
       * ─────────────────────────────────────────────────────────────────────────────
       *  ⚠️ [C1g §4] THE RANGE MOVED FROM `filters` TO `actions` — ONTO THE HEADING LINE.
       *
       *  ⚠️ THE SLOT CONFLICT THE BRIEF ANTICIPATED DOES NOT EXIST. `actions` was not passed
       *  at all on this page, and there has never been a configurator entry point on
       *  `/dashboard` — `/dashboard-layouts` is reachable only from the sidebar, deliberately
       *  a SIBLING path (`routes/index.tsx` records why). So this displaces nothing and
       *  nothing had to move for it.
       *
       *  ⚠️ NO NEW BREAKPOINT, AND NO EDIT TO `PageHeader`. Its title row is already
       *  `flex flex-wrap items-start justify-between gap-3` with a `shrink-0` right cluster,
       *  so at a narrow width the pickers wrap to their own line by the existing mechanism —
       *  "stacks below the existing breakpoint like everything else", satisfied by the
       *  primitive rather than by a rule added to it.
       *
       *  ⚠️ [amended UX1 §7] "THE SOURCE ORDER IS LOAD-BEARING FOR THE SUITE" WAS FALSE, AND
       *  HAD BEEN SINCE [C1c §5b].
       *
       *  It read: "`Dashboard.test.tsx` addresses the two pickers POSITIONALLY — both carry the
       *  same placeholder — so reordering them here would silently swap what those assertions
       *  mean." Both halves were wrong. [C1c §5b] changed `toPlaceholder` to "In 3 days", so
       *  `getAllByPlaceholderText(/today/i)` already matched exactly ONE node and the `[0]`
       *  after it was a no-op that read like a load-bearing positional assumption. The test
       *  addresses them by LABEL now, which is the fact, and reordering these two is a visual
       *  change and nothing more.
       *
       *  A comment claiming a constraint that does not exist is worse than no comment: it stops
       *  somebody making a correct change.
       *
       *  ⚠️ [amended UX1 §7] AND `items-center` NO LONGER MEETS A LABEL-OVER-CONTROL STACK.
       *  `FilterField` takes `layout="inline"` — DATA with a default, so its thirteen other
       *  consumers are byte-identical — which is what puts `From:` beside its picker on the
       *  heading row instead of floating a caption above it. The primitive's alignment is
       *  still not changed for this one caller.
       * ─────────────────────────────────────────────────────────────────────────────
       */}
      <PageHeader
        icon={LayoutDashboard}
        title={DASHBOARD_COPY.title}
        actions={
          <>
            <FilterField layout="inline" label={DASHBOARD_COPY.filters.from}>
              {/* ⚠️ [UX1 §7] THE WIDTH MOVED OFF THE FIELD AND ONTO THE CONTROL. With the label
                  inline, `w-40` on the wrapper is the width of LABEL PLUS PICKER and the picker
                  collapses. `w-36` is what `Arrangements` already gives these two. */}
              <DatePicker
                className="w-36"
                format="dd LLL yyyy"
                placeholder={DASHBOARD_COPY.fromPlaceholder}
                value={asPickerDate(range.dateFrom)}
                onChange={(value) => void setRange({ dateFrom: fromPickerDate(value) ?? '' })}
              />
            </FilterField>
            <FilterField layout="inline" label={DASHBOARD_COPY.filters.to}>
              <DatePicker
                className="w-36"
                format="dd LLL yyyy"
                placeholder={DASHBOARD_COPY.toPlaceholder}
                value={asPickerDate(range.dateTo)}
                onChange={(value) => void setRange({ dateTo: fromPickerDate(value) ?? '' })}
              />
            </FilterField>
          </>
        }
      />

      {!payload ? (
        /* ⚠️ ONE FULL-WIDTH BLOCK, not N guessed silhouettes. A guessed count is ragged at
           some rungs and not others, so it reflows into a DIFFERENT SHAPE rather than into
           the same shape with real data. */
        <Loading label={DASHBOARD_COPY.loading}>
          <Skeleton className="h-64 w-full" />
        </Loading>
      ) : payload.tiles.length === 0 ? (
        <EmptyState title={DASHBOARD_COPY.emptyTitle} description={DASHBOARD_COPY.emptyDescription} />
      ) : (
        <>
          {/*
            ⚠️ [P6b.2] ONE GRID PER ROW, NOT ONE GRID. A row ENDS where the operator said it
            ends — two small tiles they grouped stay together even though a third would fit
            beside them, which a single flowing grid cannot express. `groupTilesByRow` is the
            SHARED derivation, so this and the configurator's preview cannot disagree about
            which tiles are on row 2.
          */}
          <div className={DASHBOARD_ROW_STACK} data-testid="dashboard-grid">
            {groupTilesByRow(payload.tiles).map((group) => (
              <div key={group[0]?.instanceId ?? 'row'} className={DASHBOARD_ROW_GRID} data-testid="dashboard-row">
                {group.map((tile) => {
                  /*
                   * ⚠️ A TILE THIS BUILD DOES NOT KNOW IS SKIPPED, NEVER A CRASH. A server ahead
                   * of a client is routine during a rolling deploy, and the label map is
                   * exhaustive over what this bundle was compiled against rather than over what
                   * the API might send.
                   */
                  if (!hasRenderer(tile.id)) return null;

                  /* [P6b.2] The words and the destination ride the PLACEMENT now. */
                  const words = resolveTileWords(tile.id, tile);
                  const drillable = tile.error === null && tileIsDrillable(tile.id, tile.data);
                  const href = drillable
                    ? hrefOf(
                        DASHBOARD_TILES[tile.id].drillTo,
                        accessRole,
                        payload.range,
                        tile.id,
                        /* [P6b.1] Only a shortcut reads it; only a PLACEMENT supplies it. */
                        tile.data !== null && 'target' in tile.data ? (tile.data.target ?? null) : null,
                      )
                    : null;
                  const loading = view.isFetching || (retry.isPending && retry.variables === tile.id);

                  return (
                    <TileCard
                      /* ⚠️ [C2b §6b] The registry's own category — see `TileCard`. */
                      category={DASHBOARD_TILES[tile.id].category}
                      key={tile.instanceId}
                      id={tile.id}
                      instanceId={tile.instanceId}
                      title={words.title}
                      hint={words.hint}
                      spanClassName={TILE_SPAN[tile.size]}
                      status={loading ? 'loading' : tile.error ? 'error' : 'ready'}
                      drill={href ? { to: href, label: words.drill } : undefined}
                      drillReach={tileDrillReach(tile.id)}
                      skeleton={tileSkeleton(tile.id)}
                      error={tile.error?.message}
                      retryable={tile.error?.retryable}
                      retrying={retry.isPending && retry.variables === tile.id}
                      onRetry={() => retry.mutate(tile.id)}
                    >
                      {tile.data === null ? null : renderTile(tile.id, tile.data, actions)}
                    </TileCard>
                  );
                })}
              </div>
            ))}
          </div>
          {/* ⚠️ SAID OUT LOUD. A tile that is off is not a tile that is broken, and P6b is
              where an operator switches one on. */}
          {offCount > 0 ? <p className="text-xs text-muted-foreground">{DASHBOARD_COPY.offCount(offCount)}</p> : null}
        </>
      )}
      <ArrangementPreviewModal
        arrangementId={preview?.id ?? null}
        onClose={() => setPreview(null)}
        openedFrom={preview?.eyeKey ?? null}
      />
    </div>
  );
};

export default DashboardPage;
